import { isDefaultFreshPrintStoreLogo } from '../../helpers/storeCheckoutUiVariationBranding.utils';
import {
  STORE_LOGO_UPLOAD,
  STORE_LOGO_UPLOAD_MESSAGES,
} from '../../constants/storeCheckout.constants';

/** Accepts only horizontal logos: width must be strictly greater than height (square is rejected). */
export const isValidStoreLogoDimensions = (width: number, height: number): boolean => {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return false;
  }
  return width > height;
};

export const isValidStoreLogoMimeType = (mimeType: string): boolean =>
  (STORE_LOGO_UPLOAD.VALID_MIME_TYPES as readonly string[]).includes(mimeType);

export type StoreLogoUploadValidationError =
  | 'invalid_file_type'
  | 'invalid_dimensions'
  | 'unable_to_load';

const STORE_LOGO_UPLOAD_ERROR_MESSAGES: Record<StoreLogoUploadValidationError, string> = {
  invalid_file_type: STORE_LOGO_UPLOAD_MESSAGES.INVALID_FILE_TYPE,
  invalid_dimensions: STORE_LOGO_UPLOAD_MESSAGES.INVALID_DIMENSIONS,
  unable_to_load: STORE_LOGO_UPLOAD_MESSAGES.UNABLE_TO_LOAD,
};

export const getStoreLogoUploadErrorMessage = (error: StoreLogoUploadValidationError): string =>
  STORE_LOGO_UPLOAD_ERROR_MESSAGES[error] ?? STORE_LOGO_UPLOAD_MESSAGES.INVALID_DIMENSIONS;

export const getImageDimensions = (image: HTMLImageElement): { width: number; height: number } => ({
  width: image.naturalWidth || image.width,
  height: image.naturalHeight || image.height,
});

const readFileAsDataUrl = (file: File, onProgress?: (progress: number) => void): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const readPercent = Math.round((event.loaded / event.total) * 55);
        onProgress(15 + readPercent);
      }
    };
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        onProgress?.(75);
        resolve(reader.result);
        return;
      }
      reject(new Error('read'));
    };
    reader.onerror = () => reject(new Error('read'));
    reader.readAsDataURL(file);
  });

const waitForImageLoad = (image: HTMLImageElement, src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('load'));
    image.src = src;
  });

const ensureImageDecoded = async (image: HTMLImageElement): Promise<void> => {
  if (typeof image.decode === 'function') {
    try {
      await image.decode();
    } catch {
      /* user cancelled or decode unsupported for this source */
    }
  }
};

export type StoreLogoLoadResult =
  | { ok: true; dataUrl: string; width: number; height: number }
  | { ok: false; error: StoreLogoUploadValidationError };

export interface IStoreLogoLoadProgressOptions {
  onProgress?: (progress: number) => void;
}

/** Load file, decode image, and validate dimensions before returning a data URL. */
export const loadAndValidateStoreLogoFile = async (
  file: File,
  options?: IStoreLogoLoadProgressOptions
): Promise<StoreLogoLoadResult> => {
  const { onProgress } = options ?? {};

  if (!isValidStoreLogoMimeType(file.type)) {
    return { ok: false, error: 'invalid_file_type' };
  }

  onProgress?.(5);

  const objectUrl = URL.createObjectURL(file);
  const image = new Image();

  try {
    onProgress?.(10);
    await waitForImageLoad(image, objectUrl);
    onProgress?.(82);
    await ensureImageDecoded(image);

    const { width, height } = getImageDimensions(image);
    if (!isValidStoreLogoDimensions(width, height)) {
      return { ok: false, error: 'invalid_dimensions' };
    }

    onProgress?.(88);
    const dataUrl = await readFileAsDataUrl(file, onProgress);
    onProgress?.(100);
    return { ok: true, dataUrl, width, height };
  } catch {
    return { ok: false, error: 'unable_to_load' };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/** Re-validate an existing data URL (e.g. after restore from cache). Skips FP default SVG asset. */
export const validateStoreLogoDataUrl = (dataUrl: string): Promise<StoreLogoLoadResult> => {
  if (isDefaultFreshPrintStoreLogo(dataUrl)) {
    return Promise.resolve({ ok: true, dataUrl, width: 1920, height: 302 });
  }

  return new Promise((resolve) => {
    const image = new Image();
    image.onload = async () => {
      await ensureImageDecoded(image);
      const { width, height } = getImageDimensions(image);
      if (!isValidStoreLogoDimensions(width, height)) {
        resolve({ ok: false, error: 'invalid_dimensions' });
        return;
      }
      resolve({ ok: true, dataUrl, width, height });
    };
    image.onerror = () => resolve({ ok: false, error: 'unable_to_load' });
    image.src = dataUrl;
  });
};
